/**
 * Nova requisição de compra (ADR-0069 D11, revista na ADR-0078).
 *
 * Os TRÊS tipos de item na mesma requisição — produto do cadastro, produto
 * não cadastrado e serviço de terceiro — com obra e anexos POR ITEM. Antes
 * esta tela só aceitava produto do cadastro, e terceiro só entrava pelo
 * revisor de ferragens. A regra do que é obrigatório em cada tipo mora no
 * backend (`compras/services_itens.py`); aqui a tela só antecipa o óbvio.
 *
 * Item não cadastrado criado aqui entra em "Materiais a cadastrar" e segue
 * como texto até alguém cadastrar.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiAlertCircle, FiPaperclip, FiPlus, FiSearch, FiTrash2, FiX } from "react-icons/fi";
import styled from "styled-components";

import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  EXTENSOES_DE_ANEXO, MAX_ANEXOS_POR_ITEM, TIPOS_TERCEIRO, UNIDADES_DE_COMPRA,
  anexarNoItem, criarRequisicao, produtosParaRequisicao,
} from "../../../services/compras";
import { unidadeDefault } from "../../../services/unidades";
import { formatarQuantidade, formatarReaisBR, parseDinheiro } from "../../../utils/dinheiro";
import { Mono } from "../_shared/quadro/styles";
import { Rotulo, Segmented } from "./ui";

export const TIPO_CADASTRO = "CADASTRO";
export const TIPO_LIVRE = "LIVRE";
export const TIPO_TERCEIRO = "TERCEIRO";
const TIPOS_DE_ITEM = [
  { value: TIPO_CADASTRO, label: "Do cadastro" },
  { value: TIPO_LIVRE, label: "Não cadastrado" },
  { value: TIPO_TERCEIRO, label: "Terceiro" },
];
const CASAS_QTD = 3;

const CAMPO = {
  height: 36, padding: "0 10px", borderRadius: 8, border: "1px solid var(--border)",
  background: "var(--surface)", color: "var(--ink)", font: "inherit", fontSize: 13, width: "100%", boxSizing: "border-box",
};

const ItemBox = styled.div`
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  .topo { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .duas { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 10px; }
  .tres { display: grid; grid-template-columns: minmax(0, 1fr) 110px 110px; gap: 10px; }
  @media (max-width: 560px) { .duas, .tres { grid-template-columns: minmax(0, 1fr); } }
`;

const Sugestoes = styled.div`
  position: absolute;
  left: 0; right: 0; top: 100%;
  margin-top: 4px;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-3);
  z-index: 6;
  max-height: 260px;
  overflow-y: auto;
  > button { display: flex; justify-content: space-between; gap: 10px; width: 100%; padding: 8px 12px; background: none; border: none; border-bottom: 1px solid var(--border-subtle); color: var(--ink); font: inherit; font-size: 13px; cursor: pointer; text-align: left; }
  > button:last-child { border-bottom: 0; }
  > button:hover { background: var(--surface-hover); }
`;

function hojeMaisDiasUteis(n) {
  const d = new Date();
  let r = n;
  while (r > 0) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) r -= 1;
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function LINHA_VAZIA(tipo = TIPO_CADASTRO) {
  return {
    chave: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
    tipo, produto: null, busca: "", qtd: "1", descricao: "", unidade: "UN", tipoTerceiro: "",
    marca: "", modelo: "", especificacao: "", observacao: "", cliente: "", anexos: [],
  };
}

function FORM_INICIAL() {
  return { justificativa: "", prioridade: "NORMAL", prazo: hojeMaisDiasUteis(5), cliente: "", projeto: "", itens: [LINHA_VAZIA()] };
}

function fmtN(v) {
  return Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

/** Quantidade em pt-BR ("2,5", "1.500") ou null quando não vale. */
export function quantidadeValida(texto) {
  const n = parseDinheiro(String(texto ?? ""));
  return n !== null && n > 0 && Number(n.toFixed(CASAS_QTD)) === n ? n : null;
}

/** O que falta no item, ou "" quando está pronto para enviar. */
export function erroDoItem(it) {
  if (quantidadeValida(it.qtd) === null) return `quantidade maior que zero, com até ${CASAS_QTD} casas`;
  if (it.tipo === TIPO_CADASTRO) return it.produto ? "" : "escolha o produto do cadastro";
  if (!it.descricao.trim()) return "descreva o item";
  if (it.tipo === TIPO_TERCEIRO && !it.tipoTerceiro) return "escolha o tipo de terceiro";
  return "";
}

export function itemParaPayload(it) {
  const comum = {
    tipo: it.tipo,
    quantidade: String(quantidadeValida(it.qtd)),
    observacao: it.observacao.trim(),
    cliente_nome: it.cliente.trim(),
  };
  if (it.tipo === TIPO_CADASTRO) return { ...comum, produto_id: it.produto.id };
  if (it.tipo === TIPO_TERCEIRO) {
    return { ...comum, descricao: it.descricao.trim(), tipo_terceiro: it.tipoTerceiro, especificacao: it.especificacao.trim() };
  }
  return {
    ...comum, descricao: it.descricao.trim(), unidade: it.unidade,
    marca: it.marca.trim(), modelo: it.modelo.trim(), especificacao: it.especificacao.trim(),
  };
}

/** `inicial`: { produto, qtd } — pré-preenche a primeira linha (vem do detalhe do suprimento). */
export default function NovaRequisicao({ open, onClose, onCriada, inicial = null }) {
  const { request } = useAuth();
  const toast = useToast();
  const [unidade, setUnidade] = useState(null);
  const [form, setForm] = useState(FORM_INICIAL);
  const [erro, setErro] = useState(null);
  // Busca POR LINHA: cada item tem o seu proprio campo de produto.
  const [buscaAtiva, setBuscaAtiva] = useState(null); // chave da linha com dropdown aberto
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [abaixo, setAbaixo] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    const base = FORM_INICIAL();
    if (inicial?.produto) {
      base.itens = [{ ...LINHA_VAZIA(), produto: inicial.produto, busca: inicial.produto.nome, qtd: Number(inicial.qtd) > 0 ? String(inicial.qtd) : "1" }];
      base.justificativa = `Reposição de estoque: ${inicial.produto.codigo || inicial.produto.nome} abaixo do mínimo.`;
    }
    setForm(base);
    setErro(null);
    setBuscaAtiva(null);
    setResultados([]);
    unidadeDefault(request).then(setUnidade).catch(() => setUnidade(null));
    produtosParaRequisicao(request, { abaixoMinimo: true }).then((r) => setAbaixo(r?.produtos || [])).catch(() => setAbaixo([]));
  }, [request, open, inicial]);

  const itens = form.itens;
  const linhaAtiva = itens.find((it) => it.chave === buscaAtiva);
  const textoBusca = (linhaAtiva && !linhaAtiva.produto ? linhaAtiva.busca : "").trim();

  useEffect(() => {
    clearTimeout(timer.current);
    if (!textoBusca) { setResultados([]); setBuscando(false); return undefined; }
    setBuscando(true);
    let vale = true;
    timer.current = setTimeout(() => {
      produtosParaRequisicao(request, { busca: textoBusca })
        // Resposta de uma busca já superada não vence a mais recente.
        .then((r) => { if (vale) setResultados(r?.produtos || []); })
        .catch(() => { if (vale) setResultados([]); })
        .finally(() => { if (vale) setBuscando(false); });
    }, 300);
    return () => { vale = false; clearTimeout(timer.current); };
  }, [textoBusca, request]);

  const idsNoForm = new Set(itens.filter((it) => it.tipo === TIPO_CADASTRO && it.produto).map((it) => it.produto.id));
  const sugeridos = abaixo.filter((p) => !idsNoForm.has(p.id));

  const setItem = (chave, patch) => setForm((f) => ({ ...f, itens: f.itens.map((it) => (it.chave === chave ? { ...it, ...patch } : it)) }));
  const remover = (chave) => setForm((f) => ({ ...f, itens: f.itens.length > 1 ? f.itens.filter((it) => it.chave !== chave) : [LINHA_VAZIA()] }));
  const adicionarLinha = (tipo) => setForm((f) => ({ ...f, itens: [...f.itens, LINHA_VAZIA(tipo)] }));

  function escolherProduto(chave, p) {
    if (idsNoForm.has(p.id)) { setErro("Esse produto já está na lista."); return; }
    setErro(null);
    setItem(chave, { produto: p, busca: p.nome, qtd: Number(p.sugestao_qtd) > 0 ? String(p.sugestao_qtd) : "1" });
    setBuscaAtiva(null);
    setResultados([]);
  }

  function adicionarTodos() {
    setForm((f) => ({
      ...f,
      itens: [
        ...f.itens.filter((it) => it.produto || it.busca || it.descricao),
        ...sugeridos.map((p) => ({ ...LINHA_VAZIA(), produto: p, busca: p.nome, qtd: Number(p.sugestao_qtd) > 0 ? String(p.sugestao_qtd) : "1" })),
      ],
    }));
  }

  function anexar(chave, arquivos) {
    setForm((f) => ({
      ...f,
      itens: f.itens.map((it) => (it.chave === chave
        ? { ...it, anexos: [...it.anexos, ...Array.from(arquivos || [])].slice(0, MAX_ANEXOS_POR_ITEM) }
        : it)),
    }));
  }

  const estimativa = useMemo(() => itens.reduce((acc, it) => {
    if (it.tipo !== TIPO_CADASTRO || !it.produto) return acc;
    const preco = Number(it.produto.ultimo_preco ?? it.produto.custo_medio ?? 0);
    return acc + preco * (quantidadeValida(it.qtd) || 0);
  }, 0), [itens]);

  const preenchidos = itens.filter((it) => it.produto || it.busca.trim() || it.descricao.trim());

  async function salvar(e) {
    e?.preventDefault();
    if (!preenchidos.length) { setErro("Adicione ao menos um item."); return; }
    const comErro = preenchidos.map((it, i) => [i + 1, erroDoItem(it)]).find(([, m]) => m);
    if (comErro) { setErro(`Item ${comErro[0]}: ${comErro[1]}.`); return; }
    setErro(null);
    setSalvando(true);
    try {
      const criada = await criarRequisicao(request, {
        business_unit: unidade?.id,
        justificativa: form.justificativa || "",
        prioridade: form.prioridade,
        prazo_cotacao: form.prazo || null,
        cliente_nome: form.cliente || "",
        projeto_resumo: form.projeto || "",
        itens: preenchidos.map(itemParaPayload),
      });
      // Anexos sobem depois, um por chamada (padrão de upload do projeto);
      // a requisição já existe, então uma falha aqui não perde o pedido.
      const falhas = [];
      const criados = criada?.itens || [];
      for (let i = 0; i < preenchidos.length; i += 1) {
        for (const arquivo of preenchidos[i].anexos) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await anexarNoItem(request, criada.id, criados[i]?.id, arquivo);
          } catch (err) {
            falhas.push(`${arquivo.name}: ${err?.payload?.detalhe || err?.message || "falhou"}`);
          }
        }
      }
      if (falhas.length) toast.error(`Requisição criada, mas ${falhas.length} anexo(s) não subiram. ${falhas[0]}`);
      await onCriada();
    } catch (err) {
      setErro(err?.payload?.detalhe || err?.message || "Erro ao criar requisição.");
    } finally {
      setSalvando(false);
    }
  }

  const footer = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 12, color: "var(--ink-3)" }}>Estimativa <Mono $size={13}>{formatarReaisBR(estimativa)}</Mono></span>
      <span style={{ flex: 1 }} />
      <button type="button" className="btn btn--secondary" onClick={onClose}>Cancelar</button>
      <button type="button" className="btn btn--primary" onClick={salvar} disabled={salvando}>{salvando ? "Criando…" : "Criar requisição"}</button>
    </div>
  );

  return (
    <SlideOverPanel open={open} onClose={onClose} title="Nova requisição de compra" subtitle={`Origem manual${unidade?.codigo ? ` · ${unidade.codigo}` : ""}`} width="min(95vw, 720px)" footer={footer}>
      <form onSubmit={salvar} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <Rotulo>Prioridade</Rotulo>
            <Segmented ariaLabel="Prioridade" opcoes={[{ value: "NORMAL", label: "Normal" }, { value: "URGENTE", label: "Urgente", tone: "danger" }]} valor={form.prioridade} onChange={(v) => setForm((f) => ({ ...f, prioridade: v }))} />
          </div>
          <div>
            <Rotulo>Prazo para cotação</Rotulo>
            <input type="date" value={form.prazo} onChange={(e) => setForm((f) => ({ ...f, prazo: e.target.value }))} style={CAMPO} aria-label="Prazo para cotação" />
            <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 4 }}>padrão: +5 dias úteis</div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <Rotulo>Cliente / obra (opcional)</Rotulo>
            <input value={form.cliente} onChange={(e) => setForm((f) => ({ ...f, cliente: e.target.value }))} placeholder="Vale para todos os itens" maxLength={200} style={CAMPO} aria-label="Cliente ou obra da requisição" />
          </div>
          <div>
            <Rotulo>Projeto (opcional)</Rotulo>
            <input value={form.projeto} onChange={(e) => setForm((f) => ({ ...f, projeto: e.target.value }))} placeholder="Nº ou nome do projeto" maxLength={300} style={CAMPO} aria-label="Projeto" />
          </div>
        </div>
        <div>
          <Rotulo>Justificativa</Rotulo>
          <textarea rows={2} value={form.justificativa} onChange={(e) => setForm((f) => ({ ...f, justificativa: e.target.value }))} placeholder="Ex.: estoque abaixo do mínimo, pedido 1234 precisa" style={{ ...CAMPO, height: "auto", padding: "8px 10px", resize: "vertical" }} aria-label="Justificativa" />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Rotulo style={{ margin: 0 }}>Itens ({preenchidos.length})</Rotulo>
          {itens.map((it, indice) => {
            const p = it.produto;
            const abaixoMin = p && Number(p.estoque_minimo) > 0 && Number(p.saldo) < Number(p.estoque_minimo);
            const aberta = buscaAtiva === it.chave && !p;
            const candidatos = resultados.filter((r) => !idsNoForm.has(r.id));
            const semResultado = aberta && it.busca.trim() && !buscando && candidatos.length === 0;
            const qtd = quantidadeValida(it.qtd);
            return (
              <ItemBox key={it.chave}>
                <div className="topo">
                  <strong style={{ fontSize: 13 }}>Item {indice + 1}</strong>
                  <Segmented
                    ariaLabel={`Tipo do item ${indice + 1}`}
                    opcoes={TIPOS_DE_ITEM}
                    valor={it.tipo}
                    onChange={(v) => setItem(it.chave, { tipo: v })}
                  />
                  <span style={{ flex: 1 }} />
                  <button type="button" className="btn btn--secondary btn--sm" onClick={() => remover(it.chave)} aria-label={`Remover item ${indice + 1}`}>
                    <FiTrash2 size={12} />
                  </button>
                </div>

                {it.tipo === TIPO_CADASTRO ? (
                  <div style={{ position: "relative" }}>
                    <FiSearch size={13} style={{ position: "absolute", left: 10, top: 11, color: "var(--ink-4)" }} />
                    <input
                      value={it.busca}
                      onChange={(e) => { setItem(it.chave, { busca: e.target.value, produto: null }); setBuscaAtiva(it.chave); }}
                      onFocus={() => { if (!p) setBuscaAtiva(it.chave); }}
                      onBlur={() => setTimeout(() => setBuscaAtiva((a) => (a === it.chave ? null : a)), 180)}
                      placeholder="Código ou nome do produto"
                      aria-label={`Produto do item ${indice + 1}`}
                      style={{ ...CAMPO, paddingLeft: 30, paddingRight: p ? 30 : 10, fontWeight: p ? 600 : 400 }}
                    />
                    {p ? (
                      <button type="button" onClick={() => setItem(it.chave, { produto: null, busca: "" })} aria-label="Trocar produto" style={{ position: "absolute", right: 6, top: 8, width: 20, height: 20, border: "none", background: "none", color: "var(--ink-4)", cursor: "pointer" }}><FiX size={13} /></button>
                    ) : null}
                    {p ? (
                      <div style={{ fontSize: 11, color: abaixoMin ? "var(--danger-text)" : "var(--ink-3)", marginTop: 3 }}>
                        <Mono $size={11}>{p.codigo}</Mono> · {p.unidade} · estoque {fmtN(p.saldo)} / mín {fmtN(p.estoque_minimo)}
                        {p.ultimo_preco ? ` · último ${formatarReaisBR(p.ultimo_preco)}` : ""}
                      </div>
                    ) : null}
                    {aberta && candidatos.length ? (
                      <Sugestoes>
                        {candidatos.map((r) => (
                          <button type="button" key={r.id} onMouseDown={(e) => e.preventDefault()} onClick={() => escolherProduto(it.chave, r)}>
                            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.nome} <span style={{ color: "var(--ink-4)" }}>· {r.codigo}</span></span>
                            <span style={{ fontSize: 11, color: r.abaixo_minimo ? "var(--danger-text)" : "var(--ink-4)", whiteSpace: "nowrap" }}>{fmtN(r.saldo)} / mín {fmtN(r.estoque_minimo)}</span>
                          </button>
                        ))}
                      </Sugestoes>
                    ) : null}
                    {semResultado ? (
                      <div style={{ fontSize: 11, color: "var(--warning-text)", marginTop: 3 }}>
                        Não está no cadastro.{" "}
                        <button type="button" onClick={() => setItem(it.chave, { tipo: TIPO_LIVRE, descricao: it.busca.trim(), busca: "" })} style={{ background: "none", border: "none", padding: 0, color: "var(--accent)", cursor: "pointer", font: "inherit" }}>
                          Pedir como item não cadastrado
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <input
                    value={it.descricao}
                    onChange={(e) => setItem(it.chave, { descricao: e.target.value })}
                    maxLength={300}
                    placeholder={it.tipo === TIPO_TERCEIRO ? "Ex.: espelho fumê 200 × 118" : "Ex.: luminária quadrada 290 × 290"}
                    aria-label={`Descrição do item ${indice + 1}`}
                    style={CAMPO}
                  />
                )}

                <div className="tres">
                  {it.tipo === TIPO_TERCEIRO ? (
                    <select value={it.tipoTerceiro} onChange={(e) => setItem(it.chave, { tipoTerceiro: e.target.value })} style={CAMPO} aria-label={`Tipo de terceiro do item ${indice + 1}`}>
                      <option value="">— Tipo de terceiro —</option>
                      {TIPOS_TERCEIRO.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  ) : (
                    <input
                      value={it.cliente}
                      onChange={(e) => setItem(it.chave, { cliente: e.target.value })}
                      maxLength={200}
                      placeholder={form.cliente ? `Obra: ${form.cliente} (da requisição)` : "Obra deste item (opcional)"}
                      aria-label={`Obra do item ${indice + 1}`}
                      style={CAMPO}
                    />
                  )}
                  <input
                    value={it.qtd}
                    onChange={(e) => setItem(it.chave, { qtd: e.target.value })}
                    inputMode="decimal"
                    autoComplete="off"
                    aria-label={`Quantidade do item ${indice + 1}`}
                    aria-invalid={qtd === null}
                    placeholder="Qtd"
                    title={qtd !== null ? `Vai pedir ×${formatarQuantidade(qtd)}` : "Quantidade inválida"}
                    style={{ ...CAMPO, fontFamily: "var(--font-mono)", textAlign: "center", borderColor: qtd === null ? "var(--danger-text)" : undefined }}
                  />
                  {it.tipo === TIPO_LIVRE ? (
                    <select value={it.unidade} onChange={(e) => setItem(it.chave, { unidade: e.target.value })} style={CAMPO} aria-label={`Unidade do item ${indice + 1}`}>
                      {UNIDADES_DE_COMPRA.map((u) => <option key={u.value} value={u.value}>{u.value} — {u.label}</option>)}
                    </select>
                  ) : (
                    <input readOnly tabIndex={-1} value={it.tipo === TIPO_CADASTRO ? (p?.unidade || "") : "UN"} aria-label="Unidade" style={{ ...CAMPO, background: "var(--surface-2)", color: "var(--ink-3)", cursor: "default", textAlign: "center" }} />
                  )}
                </div>

                {it.tipo === TIPO_TERCEIRO ? (
                  <input
                    value={it.cliente}
                    onChange={(e) => setItem(it.chave, { cliente: e.target.value })}
                    maxLength={200}
                    placeholder={form.cliente ? `Obra: ${form.cliente} (da requisição)` : "Obra deste item (opcional)"}
                    aria-label={`Obra do item ${indice + 1}`}
                    style={CAMPO}
                  />
                ) : null}

                {it.tipo === TIPO_LIVRE ? (
                  <div className="duas">
                    <input value={it.marca} onChange={(e) => setItem(it.chave, { marca: e.target.value })} maxLength={120} placeholder="Marca (opcional)" aria-label={`Marca do item ${indice + 1}`} style={CAMPO} />
                    <input value={it.modelo} onChange={(e) => setItem(it.chave, { modelo: e.target.value })} maxLength={120} placeholder="Modelo / referência (opcional)" aria-label={`Modelo do item ${indice + 1}`} style={CAMPO} />
                  </div>
                ) : null}
                {it.tipo !== TIPO_CADASTRO ? (
                  <textarea rows={2} value={it.especificacao} onChange={(e) => setItem(it.chave, { especificacao: e.target.value })} placeholder="Especificação (opcional): medidas, acabamento, compatibilidade" aria-label={`Especificação do item ${indice + 1}`} style={{ ...CAMPO, height: "auto", padding: "8px 10px", resize: "vertical" }} />
                ) : null}
                <input value={it.observacao} onChange={(e) => setItem(it.chave, { observacao: e.target.value })} maxLength={300} placeholder="Observação (opcional)" aria-label={`Observação do item ${indice + 1}`} style={CAMPO} />

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", fontSize: 12 }}>
                  {it.anexos.map((a, i) => (
                    <span key={`${a.name}-${i}`} style={{ display: "inline-flex", gap: 4, alignItems: "center", minWidth: 0 }}>
                      <FiPaperclip size={12} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }} title={a.name}>{a.name}</span>
                      <button type="button" onClick={() => setItem(it.chave, { anexos: it.anexos.filter((_, j) => j !== i) })} aria-label={`Remover ${a.name}`} style={{ background: "none", border: "none", color: "var(--ink-4)", cursor: "pointer", padding: 0, display: "inline-flex" }}><FiX size={12} /></button>
                    </span>
                  ))}
                  {it.anexos.length < MAX_ANEXOS_POR_ITEM ? (
                    <label style={{ color: "var(--accent)", cursor: "pointer", display: "inline-flex", gap: 4, alignItems: "center" }}>
                      <FiPaperclip size={12} /> Anexar ({it.anexos.length}/{MAX_ANEXOS_POR_ITEM})
                      <input type="file" multiple hidden accept={EXTENSOES_DE_ANEXO} aria-label={`Anexar arquivos no item ${indice + 1}`} onChange={(e) => { anexar(it.chave, e.target.files); e.target.value = ""; }} />
                    </label>
                  ) : null}
                </div>
              </ItemBox>
            );
          })}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => adicionarLinha(TIPO_CADASTRO)}><FiPlus size={12} /> Item do cadastro</button>
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => adicionarLinha(TIPO_LIVRE)}><FiPlus size={12} /> Item não cadastrado</button>
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => adicionarLinha(TIPO_TERCEIRO)}><FiPlus size={12} /> Item de terceiro</button>
          </div>
        </div>

        {sugeridos.length ? (
          <div style={{ background: "var(--accent-soft)", color: "var(--accent)", borderRadius: 10, padding: "10px 14px", fontSize: 13, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ flex: 1 }}>Sugestão: <strong>{sugeridos.length} produto{sugeridos.length === 1 ? " está" : "s estão"} abaixo do mínimo</strong> nesta unidade.</span>
            <button type="button" className="btn btn--primary btn--sm" onClick={adicionarTodos}><FiPlus size={12} /> Adicionar todos</button>
          </div>
        ) : null}

        {erro ? <div role="alert" style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--danger-text)", fontSize: 13 }}><FiAlertCircle size={14} /> {erro}</div> : null}
      </form>
    </SlideOverPanel>
  );
}
